import { useParams } from "react-router-dom";
import { SectionHeader } from "../../../components/SectionHeader/SectionHeader";
import { useScheduleFetch } from "../hooks/useScheduleFetch";

import classes from "./ScheduleView.module.scss";
import { useMemo } from "react";
import moment from "moment";
import { JobFrequencyEnum } from "../../Sales/Contracts/CreateJobAndSchedule/CreateJobAndSchedule.model";

function generateRecurrenceText({
  recurEvery,
  frequency,
  startTime,
}: {
  startTime: Date;
  frequency: JobFrequencyEnum;
  recurEvery: number;
}) {
  const frequencyMap = {
    [JobFrequencyEnum.WEEKLY]: "Week" + (recurEvery > 1 ? "s" : ""),
    [JobFrequencyEnum.MONTHLY]: "Month" + (recurEvery > 1 ? "s" : ""),
    [JobFrequencyEnum.YEARLY]: "Year" + (recurEvery > 1 ? "s" : ""),
  };

  return `Every ${recurEvery} ${frequencyMap[frequency]} On ${moment(
    startTime
  ).format("dddd")}`;
}

export function ScheduleView() {
  const { scheduleId } = useParams();

  const { scheduleData } = useScheduleFetch(Number(scheduleId));

  const displayData = useMemo(() => {
    if (scheduleData) {
      return {
        supervisorZone: scheduleData.jobZone.name,
        employees: scheduleData.employees
          .map((e) => e.firstName + " " + e.lastName)
          .join(", "),
        jobName: scheduleData.jobDetail.name,
        duration:
          scheduleData.duration +
          (Number(scheduleData.duration) > 1 ? " Hours" : " Hour"),
        startTime: moment(scheduleData.startTime).format("MM/DD/YYYY hh:mm A"),
        endTime: moment(scheduleData.endTime).format("MM/DD/YYYY hh:mm A"),
        lateInAlertTime: scheduleData.lateInAlertTime + " min",
        lateOutAlertTime: scheduleData.lateOutAlertTime + " min",
        recurrence:
          scheduleData.jobDetail.recurrence &&
          generateRecurrenceText({
            startTime: scheduleData.startTime,
            frequency: scheduleData.jobDetail.frequency,
            recurEvery: scheduleData.jobDetail.recurEvery,
          }),
        attachments: JSON.parse(scheduleData.jobDetail.attachment2Path),
        dailyWorkForm: JSON.parse(scheduleData.jobDetail.dailyWorkForm),
      };
    }
  }, [scheduleData]);

  return displayData ? (
    <>
      <SectionHeader showBackButton title="Schedule Details" />
      <div className={classes.fields}>
        <div className={classes.field}>
          <p className={classes.name}>Service Division</p>
          <p className={classes.info}>{displayData?.supervisorZone}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>Employees</p>
          <p className={classes.info}>{displayData?.employees}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>Job Name</p>
          <p className={classes.info}>{displayData?.jobName}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>Duration</p>
          <p className={classes.info}>{displayData?.duration}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>Start Time</p>
          <p className={classes.info}>{displayData?.startTime}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>End Time</p>
          <p className={classes.info}>{displayData?.endTime}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>Late In alerts</p>
          <p className={classes.info}>{displayData?.lateInAlertTime}</p>
        </div>
        <div className={classes.field}>
          <p className={classes.name}>Late Out Alerts</p>
          <p className={classes.info}>{displayData?.lateOutAlertTime}</p>
        </div>
        {displayData.recurrence && (
          <div className={classes.field}>
            <p className={classes.name}>Recurrence</p>
            <p className={classes.info}>{displayData?.recurrence}</p>
          </div>
        )}
        {displayData.attachments?.length ? (
          <div className={classes.field}>
            <p className={classes.name}>Attachments</p>
            <p className={classes.info}>
              {displayData.attachments.map((a: string, i: number) => (
                <div>
                  <a
                    key={i}
                    target="_blank"
                    rel="noreferrer"
                    href={`${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${a}`}
                  >
                    {a.split("/").at(-1)}
                  </a>
                </div>
              ))}
            </p>
          </div>
        ) : null}
        {displayData.dailyWorkForm?.length ? (
          <div className={classes.field}>
            <p className={classes.name}>Daily Work Form</p>
            <p className={classes.info}>
              {displayData.dailyWorkForm.map((a: string, i: number) => (
                <div>
                  <a
                    key={i}
                    target="_blank"
                    rel="noreferrer"
                    href={`${process.env.REACT_APP_DSC_SERVICE_DOMAIN}/${a}`}
                  >
                    {a.split("/").at(-1)}
                  </a>
                </div>
              ))}
            </p>
          </div>
        ) : null}
      </div>
    </>
  ) : null;
}
